/* eslint-disable react/no-array-index-key */
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import clsx from "clsx";
import React, {
  useCallback,
  useEffect,
  useState,
  forwardRef,
  useRef,
  ReactNode,
} from "react";
import { createUseStyles } from "react-jss";
import {
  usePagination,
  useSortBy,
  useRowSelect,
  useTable,
  useExpanded,
} from "react-table";
import ArrowDownSLineIcon from "remixicon-react/ArrowDownSLineIcon";
import ArrowUpSLineIcon from "remixicon-react/ArrowUpSLineIcon";
import { KeyboardArrowRight } from "@mui/icons-material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import { theme } from "$utils/theme/bataillog";
import { Button, Paper } from "$components";
import JDebounceInput from "../JDebounceInput/JDebounceInput";
import JLoader from "../JLoader/JLoader";
import usePersistedState from "$hooks/usePersistedState";
import RowsPerPage from "$components/RowsPerPage";

type CustomTableProps = {
  data: Array<any>;
  columns: Array<any>;
  fetchData?: () => void;
  isFetching?: boolean;
  pageCount?: number;
  pageIndex?: number;
  filters?: any;
  // eslint-disable-next-line no-unused-vars
  setFilter?: (name: string, value: any) => void;
  // eslint-disable-next-line no-unused-vars
  setPageIndex: (index: number) => void;
  renderRowSubComponent?: any;
  isSubTable?: boolean;
  hideFilters?: boolean;
  hideCheckboxes?: boolean;
  onClickAction?: any;
  setSelectedItem?: any;
  setSelectedRows?: any;
  selectedRows?: any[];
  rowProps?: (row: any) => any;
  hasActionRow?: boolean;
  actionRowNumber?: number;
  pageName?: string;
  noResultsText?: string;
  noDataText?: string;
  children?: ReactNode; // if there is some children, display them instead of the table body
  usePaper?: boolean;
  rowsPerPage?: number | null;
  setRowsPerPage?: (rows: number) => void;
};

const useCombinedRefs = (...refs: any[]): React.MutableRefObject<any> => {
  const targetRef = useRef();

  useEffect(() => {
    refs.forEach((ref) => {
      if (!ref) return;

      if (typeof ref === "function") {
        ref(targetRef.current);
      } else {
        // eslint-disable-next-line no-param-reassign
        ref.current = targetRef.current;
      }
    });
  }, [refs]);

  return targetRef;
};

// Helper function to get row ID (supports both _id and id)
const getRowId = (row: any): string | number | undefined => {
  return row?._id ?? row?.id;
};

// Helper function to check if a row is selected by comparing _id or id
const isRowSelected = (row: any, selectedRows: any[]): boolean => {
  if (!row?.original || !selectedRows || selectedRows.length === 0) {
    return false;
  }
  const rowId = getRowId(row.original);
  if (!rowId) {
    // Fallback to reference equality if neither _id nor id is available
    return selectedRows.includes(row.original);
  }
  return selectedRows.some((selectedRow) => getRowId(selectedRow) === rowId);
};

interface IndeterminateCheckboxProps {
  indeterminate?: boolean;
  name?: string;
  disabled?: boolean;
  style?: any;
  row?: any;
  selectedRows?: any[];
  checked?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}
const IndeterminateCheckbox = forwardRef<
  HTMLInputElement,
  IndeterminateCheckboxProps
>(
  (
    {
      indeterminate,
      disabled = false,
      style = {},
      selectedRows = [],
      row,
      ...rest
    },
    ref: React.Ref<HTMLInputElement>
  ) => {
    const defaultRef = useRef(null);
    const combinedRef = useCombinedRefs(ref, defaultRef);

    useEffect(() => {
      if (combinedRef?.current) {
        combinedRef.current.indeterminate = indeterminate ?? false;
      }
    }, [combinedRef, indeterminate]);

    const overridenStyle = disabled ? { cursor: "default" } : style;

    const { checked } = rest;

    return (
      <input
        type="checkbox"
        ref={combinedRef}
        style={overridenStyle}
        disabled={disabled}
        {...rest}
        checked={
          row && selectedRows.length > 0
            ? isRowSelected(row, selectedRows)
            : checked
        }
      />
    );
  }
);

function JTable({
  data,
  columns,
  isFetching = false,
  pageCount: controlledPageCount,
  setFilter,
  filters,
  pageIndex,
  setPageIndex,
  renderRowSubComponent,
  isSubTable = false,
  hideFilters = false,
  hideCheckboxes = false,
  onClickAction = () => {},
  setSelectedItem,
  setSelectedRows,
  selectedRows = [],
  rowProps = () => {},
  hasActionRow = false,
  actionRowNumber = 1,
  pageName = "default",
  noResultsText = "Aucun résultat ne correspond à votre recherche",
  noDataText = "Aucune donnée n'a encore été créée",
  children,
  usePaper = true,
  rowsPerPage = null,
  setRowsPerPage = null,
}: CustomTableProps) {
  // Track when we're clearing selections to prevent re-adding during the clear operation
  const isClearingRef = useRef(false);
  // Track previous selectedRows length to detect when it goes from non-empty to empty
  const prevSelectedRowsLengthRef = useRef(selectedRows.length);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    toggleRowSelected,
    toggleAllRowsSelected,
    rows,
  } = useTable(
    {
      columns,
      data,
      autoResetPage: false,
      manualPagination: true,
      initialState: { pageIndex, pageSize: 10 },
      pageCount: controlledPageCount,
      stateReducer: (newState, action, previousState, instance) => {
        if (setSelectedRows) {
          switch (action.type) {
            case "toggleRowSelected": {
              const targetRow = instance.flatRows.find(
                (flatRow, index) => parseInt(action.id, 10) === index
              );
              if (targetRow) {
                const targetRowId = getRowId(targetRow.original);
                if (action.value) {
                  // Add to selectedRows if not already present (by _id or id)
                  setSelectedRows((prev) => {
                    // Only block if we're currently in the clearing window
                    // After the clearing window expires, allow normal selection (user can click)
                    if (isClearingRef.current) {
                      return prev;
                    }
                    const isAlreadySelected = prev.some(
                      (row) => getRowId(row) === targetRowId
                    );
                    if (isAlreadySelected) {
                      return prev;
                    }
                    return [...prev, targetRow.original];
                  });
                } else {
                  // Remove from selectedRows by _id or id
                  setSelectedRows((prev) =>
                    prev.filter((row) => getRowId(row) !== targetRowId)
                  );
                }
              }
              return {
                ...newState,
              };
            }
            case "toggleAllRowsSelected": {
              if (action.value) {
                // Sélectionner toutes les lignes visibles
                const allVisibleRows = instance.flatRows.map(
                  (flatRow) => flatRow.original
                );
                setSelectedRows((prev) => {
                  // Only block if we're currently in the clearing window
                  if (isClearingRef.current) {
                    return prev;
                  }
                  const prevIds = new Set(
                    prev.map((row) => getRowId(row)).filter((id) => id)
                  );
                  const newRows = allVisibleRows.filter(
                    (row) => getRowId(row) && !prevIds.has(getRowId(row))
                  );
                  return [...prev, ...newRows];
                });
              } else {
                // Deselect all visible rows
                const visibleRowIds = new Set(
                  instance.flatRows.map((flatRow) => getRowId(flatRow.original))
                );
                setSelectedRows((prev) =>
                  prev.filter((row) => !visibleRowIds.has(getRowId(row)))
                );
              }
              return {
                ...newState,
              };
            }

            default:
              return newState;
          }
        }
      },
    },
    useSortBy,
    useExpanded,
    (hooks) => {
      if (!hideCheckboxes) {
        hooks.visibleColumns.push((rescolumns: any) => [
          {
            hidden: isSubTable,
            id: "selection",
            Header: !isSubTable
              ? ({ getToggleAllRowsSelectedProps, rows: headerRows }: any) => {
                  // Calculate if all visible rows are selected (by _id or id)
                  const visibleRowIds = new Set(
                    headerRows
                      .map((row: any) => getRowId(row.original))
                      .filter((id: any) => id)
                  );
                  const selectedIds = new Set(
                    selectedRows
                      .map((row: any) => getRowId(row))
                      .filter((id: any) => id)
                  );
                  const allSelected =
                    visibleRowIds.size > 0 &&
                    Array.from(visibleRowIds).every((id: string | number) =>
                      selectedIds.has(id)
                    );
                  const someSelected =
                    visibleRowIds.size > 0 &&
                    Array.from(visibleRowIds).some((id: string | number) =>
                      selectedIds.has(id)
                    );

                  const toggleAllProps = getToggleAllRowsSelectedProps();
                  return (
                    <div>
                      <IndeterminateCheckbox
                        {...toggleAllProps}
                        indeterminate={someSelected && !allSelected}
                        checked={allSelected}
                      />
                    </div>
                  );
                }
              : () => null,
            width: 30,
            maxWidth: 30,
            minWidth: 30,
            preventClickAction: true,
            Cell: !isSubTable
              ? ({ row }: any) => {
                  const { disabled } = row.getRowProps(rowProps(row));
                  return (
                    <div>
                      <IndeterminateCheckbox
                        disabled={disabled}
                        selectedRows={selectedRows}
                        row={row}
                        {...row.getToggleRowSelectedProps()}
                      />
                    </div>
                  );
                }
              : () => null,
          },
          ...rescolumns,
        ]);
      } else {
        hooks.visibleColumns.push((rescolumns: any) => [...rescolumns]);
      }
    },
    usePagination,
    useRowSelect
  );

  const classes = useStyles();

  const [showFilters, setShowFilters] = useState(true);

  const [persistedPagination, setPersistedPagination] = usePersistedState(
    0,
    `${pageName}Page-pagination`,
    null
  );

  const handleChangeFilter = useCallback(
    (name, value) => {
      // TODO ca va faire deux fetchData, mais super compliqué a optimiser,
      // il faut un debounce mais aussi modifier le fetchData pour que la fonction ne change jamais
      setPersistedPagination(0);
      gotoPage(0);
      setFilter?.(name, value);
    },
    [gotoPage, setFilter, setPersistedPagination]
  );

  useEffect(() => {
    // Problème déjà rencontré
    // On a un useEffect qui regarde la pagination persistante et qui change la valeur
    // si elle est différente du state local, sauf que dans le cas d'un tableau avec un sous-tableau,
    // on passe deux fois le même state pour la pagination à deux JTable différents sur une même page.
    // donc on vérifie que le tableau ne soit pas un sous tableau (d'où le !isSubTable) pour changer le state
    // Guard: during error states we sometimes get `pageCount=0`.
    // Syncing pageIndex back to a persisted value in that case can lead to
    // rapid page flipping + extra refetches (and an empty preview).
    if (
      controlledPageCount > 0 &&
      pageIndex !== persistedPagination &&
      !isSubTable
    ) {
      setPageIndex(persistedPagination);
    }
  }, [
    controlledPageCount,
    persistedPagination,
    pageIndex,
    setPageIndex,
    isSubTable,
  ]);

  // Sync react-table's selection state with selectedRows when selectedRows or rows change
  // This ensures that previously selected rows are automatically selected when they become visible again
  // Also clears react-table's selection when selectedRows is empty
  useEffect(() => {
    if (!hideCheckboxes && setSelectedRows) {
      const prevLength = prevSelectedRowsLengthRef.current;
      const currentLength = selectedRows.length;

      const wasCleared = prevLength > 0 && currentLength === 0;

      if (wasCleared) {
        isClearingRef.current = true;
        setTimeout(() => {
          isClearingRef.current = false;
        }, 1000);
      }

      prevSelectedRowsLengthRef.current = currentLength;

      if (rows.length === 0) {
        return;
      }

      if (selectedRows.length === 0) {
        const hasSelectedRows = rows.some((row) => row.isSelected);
        if (hasSelectedRows) {
          toggleAllRowsSelected(false);
        }
        // When selectedRows is empty, don't do anything else
        // This prevents re-adding rows when data updates after clearing
      } else {
        // Don't sync if we're currently clearing (prevent re-adding during clear)
        if (isClearingRef.current) {
          return;
        }
        // Sync selections: select rows that are in selectedRows but not selected in react-table
        const selectedIds = new Set(
          selectedRows.map((row) => getRowId(row)).filter((id) => id)
        );

        rows.forEach((row) => {
          const rowId = getRowId(row.original);
          if (rowId) {
            if (selectedIds.has(rowId) && !row.isSelected) {
              // Select rows that are in selectedRows but not selected in react-table
              toggleRowSelected(row.id, true);
            } else if (!selectedIds.has(rowId) && row.isSelected) {
              // Deselect rows that are not in selectedRows but selected in react-table
              toggleRowSelected(row.id, false);
            }
          }
        });
      }
    }
  }, [
    selectedRows,
    rows,
    hideCheckboxes,
    setSelectedRows,
    toggleRowSelected,
    toggleAllRowsSelected,
  ]);

  // buttons pagination

  let buttons: any[];

  if (controlledPageCount <= 7) {
    buttons = Array.from({ length: controlledPageCount }).map(
      (l, index) => index
    );
  } else if (pageIndex < 4) {
    buttons = [
      ...Array.from({ length: 5 }).map((l, index) => index),
      -1,
      controlledPageCount - 1,
    ];
  } else if (pageIndex > controlledPageCount - 5) {
    buttons = [
      0,
      -1,
      ...Array.from({ length: 5 }).map(
        (l, index) => index + controlledPageCount - 5
      ),
    ];
  } else {
    buttons = [
      0,
      -1,
      pageIndex - 1,
      pageIndex,
      pageIndex + 1,
      -1,
      controlledPageCount - 1,
    ];
  }

  return (
    <>
      <TableContainer
        className={
          !isSubTable
            ? clsx(classes.tableContainer)
            : clsx(classes.subTableContainer)
        }
        component={usePaper ? Paper : undefined}
      >
        <Table aria-label="custom pagination table" {...getTableProps()}>
          {!isSubTable && (
            <TableHead
              className={
                !isSubTable ? classes.tableTitle : classes.subTableTitle
              }
            >
              {headerGroups.map((headerGroup, index) => (
                <TableRow
                  {...headerGroup.getHeaderGroupProps()}
                  key={`header-${index}-${!isSubTable ? "main" : "secondary"}`}
                >
                  {headerGroup.headers.map((column: any, cindex) =>
                    column.hidden ? null : (
                      <TableCell
                        key={`column-${cindex}-${
                          !isSubTable ? "main" : "secondary"
                        }`}
                        style={{
                          minWidth: column.minWidth,
                          maxWidth: column.maxWidth,
                          width: column.width,
                        }}
                        className={clsx(classes.tableCellTopHead)}
                        padding="none"
                      >
                        <div
                          className={classes.tophead}
                          style={{
                            justifyContent:
                              !isSubTable &&
                              cindex + 1 === headerGroup.headers.length
                                ? "flex-end"
                                : "space-between",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {(isSubTable ||
                            cindex + 1 !== headerGroup.headers.length) && (
                            <span
                              className="th-label"
                              {...column.getHeaderProps(
                                column.getSortByToggleProps()
                              )}
                            >
                              {column.render("Header")}
                              {column.isSorted &&
                                (column.isSortedDesc ? (
                                  <i className="ml fas fa-sort-down" />
                                ) : (
                                  <i className="ml fas fa-sort-up" />
                                ))}
                            </span>
                          )}

                          {!isSubTable &&
                            cindex + 1 === headerGroup.headers.length &&
                            !hideFilters && (
                              <Button
                                onClick={() => setShowFilters(!showFilters)}
                                variant="text"
                                style={{
                                  textTransform: "none",
                                  minWidth: 20,
                                  marginLeft: 4,
                                  border: "none",
                                  padding: 4,
                                  textAlign: "center",
                                  borderRadius: "100%",
                                  position: "relative",
                                  top: -2,
                                }}
                              >
                                {showFilters ? (
                                  <ArrowUpSLineIcon
                                    size={25}
                                    style={{ margin: 0 }}
                                  />
                                ) : (
                                  <ArrowDownSLineIcon
                                    size={25}
                                    style={{ margin: 0 }}
                                  />
                                )}
                              </Button>
                            )}
                        </div>
                      </TableCell>
                    )
                  )}
                </TableRow>
              ))}
            </TableHead>
          )}

          {!isSubTable && showFilters && (
            <TableHead className={classes.tableSearch}>
              {headerGroups.map((headerGroup, index) => (
                <TableRow
                  {...headerGroup.getHeaderGroupProps()}
                  key={`header-2-${index}`}
                >
                  {headerGroup.headers.map((column: any, cindex) =>
                    column.hidden ? null : (
                      <TableCell
                        key={`column-2-${cindex}`}
                        style={{
                          minWidth: column.minWidth,
                          maxWidth: column.maxWidth,
                          width: column.width,
                        }}
                        padding="none"
                      >
                        <div className={classes.subheadPaddingXs}>
                          {column.filterName &&
                            (column.filterComponent ? (
                              column.filterComponent({
                                value: filters[column.filterName],
                                onChange: (value: any) =>
                                  column.customFormat
                                    ? handleChangeFilter(
                                        column.filterName,
                                        column.customFormat(value)
                                      )
                                    : handleChangeFilter(
                                        column.filterName,
                                        value
                                      ),
                              })
                            ) : (
                              <JDebounceInput
                                className="input is-filter"
                                // placeholder={column.render('Header')}
                                value={filters[column.filterName]}
                                onChange={(value: any) =>
                                  handleChangeFilter(column.filterName, value)
                                }
                              />
                            ))}
                        </div>
                      </TableCell>
                    )
                  )}
                </TableRow>
              ))}
            </TableHead>
          )}
          {!children && (
            <TableBody {...getTableBodyProps()}>
              {!hasActionRow && data.length === 0 && isFetching && (
                <TableRow className={clsx(classes.tableRow)} key="loader">
                  <TableCell colSpan={100} className={clsx(classes.tableCell)}>
                    <div
                      className={classes.tdPaddingXs}
                      style={{ display: "flex", justifyContent: "center" }}
                    >
                      <JLoader isScreen permanant />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!hasActionRow && !isFetching && !page.length && (
                <TableRow className={clsx(classes.tableRow)} key="no-result">
                  <TableCell colSpan={100} className={clsx(classes.tableCell)}>
                    <div
                      className={classes.tdPaddingXs}
                      style={{ display: "flex", justifyContent: "center" }}
                    >
                      <span className="table-no-result">
                        {filters && Object.keys(filters)?.length
                          ? noResultsText
                          : noDataText}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {page.map((row: any, index) => {
                prepareRow(row);
                return (
                  <React.Fragment key={index}>
                    <TableRow
                      {...row.getRowProps(rowProps(row))}
                      className={
                        !row.isExpanded
                          ? clsx(classes.tableRow)
                          : clsx(classes.tableRowExpanded)
                      }
                      key={`row-${index}${row.isExpanded ? "-e-" : ""}-${
                        !isSubTable ? "main" : "secondary"
                      }`}
                      data-tr={row.name}
                    >
                      {row.cells.map((cell: any, cellIndex: number) =>
                        cell.column.hidden ? null : (
                          <TableCell
                            padding="none"
                            {...cell.getCellProps({
                              className: cell.column.className,
                            })}
                            key={`cell-${cellIndex}-${
                              !isSubTable ? "main" : "secondary"
                            }`}
                            data-cy={row.name}
                            data-row={cell.column.name}
                            onClick={
                              !cell.column.preventClickAction
                                ? () => {
                                    onClickAction();
                                    if (setSelectedItem) {
                                      setSelectedItem(row.original);
                                    }
                                  }
                                : undefined
                            }
                            style={{
                              minWidth: cell.column.minWidth,
                              maxWidth: cell.column.maxWidth,
                              width: cell.column.width,
                            }}
                            className={clsx(classes.tableCell)}
                          >
                            <div className={classes.tdPaddingXs}>
                              {cell.render("Cell")}
                            </div>
                          </TableCell>
                        )
                      )}
                    </TableRow>

                    {row.isExpanded ? (
                      <TableRow
                        {...row.getRowProps()}
                        className={clsx(classes.subTableRow)}
                        key={`row-2-${index}`}
                        data-tr={`${row.name}-2`}
                      >
                        <TableCell colSpan={100}>
                          {renderRowSubComponent({ row })}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                );
              })}
              {hasActionRow &&
                data.length === actionRowNumber &&
                isFetching && (
                  <TableRow className={clsx(classes.tableRow)} key="loader-2">
                    <TableCell
                      colSpan={100}
                      className={clsx(classes.tableCell)}
                    >
                      <div
                        className={classes.tdPaddingXs}
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        <JLoader isScreen permanant />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              {hasActionRow &&
                !isFetching &&
                data.length === actionRowNumber && (
                  <TableRow
                    className={clsx(classes.tableRow)}
                    key="no-result-2"
                  >
                    <TableCell
                      colSpan={100}
                      className={clsx(classes.tableCell)}
                    >
                      <div
                        className={classes.tdPaddingXs}
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        <span className="table-no-result">
                          {filters && Object.keys(filters)?.length
                            ? noResultsText
                            : noDataText}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          )}
        </Table>
        {children}
      </TableContainer>

      {!isSubTable && page.length > 0 && controlledPageCount !== 1 && (
        <div className={classes.paginationFooter}>
          <IconButton
            onClick={() => {
              previousPage();
              setPersistedPagination(pageIndex - 1);
            }}
            disabled={!canPreviousPage || isFetching}
            aria-label="previous page"
          >
            <KeyboardArrowLeft />
          </IconButton>

          {buttons.map((i, index) => {
            return i === -1 ? (
              <span key={`page-ellipsis-${i}-${index}`}>...</span>
            ) : (
              <Button
                key={`page-${i}-${index}`}
                disabled={isFetching}
                onClick={() => {
                  gotoPage(i);
                  setPersistedPagination(i);
                }}
                className={`${pageIndex !== i && "active"} page`}
              >
                <span
                  style={{
                    padding: `0 ${(i + 1).toString().length >= 3 ? 4 : 0}px`,
                  }}
                >
                  {i + 1}
                </span>
              </Button>
            );
          })}

          <IconButton
            onClick={() => {
              nextPage();
              setPersistedPagination(pageIndex + 1);
            }}
            disabled={!canNextPage || isFetching}
            aria-label="next page"
          >
            <KeyboardArrowRight />
          </IconButton>
        </div>
      )}
      {rowsPerPage && (
        <div style={{ float: "right" }}>
          <RowsPerPage
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            label="OP par page"
          />
        </div>
      )}
    </>
  );
}

// eslint-disable-next-line no-unused-vars
const useStyles = createUseStyles(() => ({
  tableContainer: {
    padding: 16,
    width: "auto !important",
    overflowX: "auto !important",
    "& td.disabled, & td.disabled a": {
      color: "#B3BAC5 !important",
    },
    flex: "1",
    flexDirection: "column",
    "& td:last-child, & th:last-child": {
      position: "sticky",
      right: 0,
      // borderLeft: "2px solid #ddd",
    },
    "& th:last-child": {
      backgroundColor: "#F4F5F7",
    },
    "& td:last-child": {
      backgroundColor: "#ffffff",
    },
    "& tr:hover > td:last-child": {
      backgroundColor: "#F4F5F7",
    },
  },
  subTableContainer: {
    width: "auto !important",
    overflowX: "auto !important",
    minHeight: "auto !important",
    "& td.disabled, & td.disabled a": {
      color: "#B3BAC5 !important",
    },
    flex: "1",
    backgroundColor: "#F4F5F7",
  },
  tableTitle: {
    textTransform: "uppercase",
    backgroundColor: "#F4F5F7",
    borderRadius: "3px 3px 0px 0px",
    "& th": {
      color: "#7A869A",
      fontFamily: "Work Sans, sans-serif",
    },
    "& th, td": {
      lineHeight: "16px",
      padding: "4px 12px 0px 12px !important",
      fontSize: 13,
    },
  },
  subTableTitle: {
    backgroundColor: "#f4f5f7",
    "& th": {
      color: "#5c677a",
    },
    "& th, td": {
      lineHeight: "18px",
      padding: "4px 12px 0px 12px !important",
    },
  },
  tableSearch: {
    backgroundColor: "#F4F5F7",
    top: "44px !important",
    "& th, td": {
      // padding: '0px 18px 9px 18px !important',
      padding: "0px 12px 9px 12px !important",
      // '& div[class*="select-"]': {
      backgroundColor: "#f4f5f7 !important",
      // },
    },
  },
  tableLink: {
    color: "#030F85",
    fontWeight: "bold",
    textDecoration: "none",
  },
  tableFooter: {
    position: "sticky",
    left: 0,
    bottom: 0,
    "& td": {
      border: "none !important",
      paddingBottom: 7,
      paddingtop: 20,
      paddingLeft: 0,
    },
    "& tr": {
      border: "none",
    },
  },
  tableCellHead: {
    "&.MuiTableCell-root": {
      fontFamily: theme.font.workSans,
      // wordBreak: "break-word",
    },
  },
  tableCell: {
    "&.MuiTableCell-root": {
      fontFamily: theme.font.workSans,
      // wordBreak: "break-word",
    },
  },
  tableRow: {
    cursor: "pointer",
    "&.nopointer": {
      cursor: "default !important",
    },
    "&:hover": {
      backgroundColor: "#F4F5F7",
      '& div[class*="select-"]': {
        backgroundColor: "#f4f5f7 !important",
      },
    },
  },
  tableRowExpanded: {
    cursor: "pointer",
    "&.nopointer": {
      cursor: "default !important",
    },
    "& > td": {
      backgroundColor: "#e4e5e8",
      border: "none",
    },
  },
  subTableRow: {
    cursor: "pointer",
    "&.nopointer": {
      cursor: "default !important",
    },
    "& > td": {
      padding: "0 !important",
      border: "none",
      "& tr > td:first-child": {
        paddingLeft: "55px !important",
      },
      "& tr > th:first-child": {
        paddingLeft: "65px !important",
      },
    },
    "& td": {
      padding: "0",
    },
    '& td > div[class*="tdPadding"]': {
      minHeight: 35,
    },
  },
  CircularProgress: {
    display: "flex",
    justifyContent: "center",
    marginTop: 10,
  },
  BodyMissingTextContainer: {
    textAlign: "center",
    padding: "15px",
  },
  relative: {
    position: "relative",
  },
  tableCellTopHead: {
    borderBottom: "none !important;",
  },
  tdPaddingXs: {
    // padding: '4px 18px',
    padding: "0px 12px",
    minHeight: 50,
    display: "flex",
    alignItems: "center",
    wordBreak: "break-word",
    wordWrap: "break-word",
  },
  tophead: {
    minHeight: 40,
  },
  subheadPaddingXs: {
    padding: 0,
  },
  paginationFooter: {
    display: "flex",
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
    paddingTop: 16,
    paddingBottom: 16,
    textAlign: "right",
    "& button": {
      fontSize: 14,
      fontWeight: "bold",
      minWidth: 24,
      padding: 0,
      margin: "0 4px",
      "&.page": {
        padding: 0,
      },
      "&.active": {
        color: "#457DD9",
        background: "#457DD926 0% 0% no-repeat padding-box",
      },
      "&:hover": {
        color: "#457DD9",
        background: "#457DD926 0% 0% no-repeat padding-box",
      },
    },
  },
}));

export default JTable;
